import { Form, Modal, Select, Switch, DatePicker } from '@maxtropy/components';
import React, { useEffect, useState } from 'react';
import {
  apiV2MicrogridResolvedEditStrategyConfigPost,
  apiV2MicrogridResolvedGetStrategyConfigPost,
  apiV2MicrogridResolvedGetStrategyLastConfigTimePost,
} from '@maxtropy/intelligent-dispath-apis-v2';
import { useRequest } from 'ahooks';
import { SolveResolutionType, SolveResolutionTypeDisplay } from '../util';
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import { Spin } from 'antd';
import styles from './index.module.scss';

interface Props {
  strategyId?: number;
  onConfirm?: () => void;
  onCancel?: () => void;
}

const AlgorithmConfigurationModal = ({ onCancel, onConfirm, strategyId }: Props) => {
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const solveSwitch: boolean = Form.useWatch('solveSwitch', form);

  const { loading, data: detail } = useRequest(
    async () => {
      return apiV2MicrogridResolvedGetStrategyConfigPost({
        strategyId,
      });
    },
    {
      refreshDeps: [strategyId],
    }
  );
  const { data: { lastTime } = { lastTime: undefined } } = useRequest(
    async () => {
      return apiV2MicrogridResolvedGetStrategyLastConfigTimePost({
        id: strategyId,
      });
    },
    {
      refreshDeps: [strategyId],
    }
  );

  useEffect(() => {
    if (!detail) return;
    form.setFieldsValue({
      solveSwitch: !!detail?.solveSwitch,
      solveStartTime: detail.solveStartTime ? dayjs(detail.solveStartTime) : undefined,
      solveResolution: detail.solveResolution,
    });
  }, [detail, form, lastTime]);

  const disabledDate: RangePickerProps['disabledDate'] = current => {
    if (!current || !lastTime) {
      return false;
    }
    return current.isBefore(dayjs(lastTime).startOf('day'));
  };

  const disabledTime = (current: dayjs.Dayjs | null) => {
    if (!current || !lastTime) {
      return {};
    }

    const isSameDay = current.isSame(dayjs(lastTime), 'day');

    return {
      disabledHours: () =>
        isSameDay ? Array.from({ length: 24 }, (_, i) => i).filter(hour => hour < dayjs(lastTime).hour()) : [], // 如果不是同一天，不禁用任何小时
      disabledMinutes: () => [],
      disabledSeconds: () => [],
    };
  };

  const onOk = () => {
    form.validateFields().then(value => {
      setConfirmLoading(true);

      apiV2MicrogridResolvedEditStrategyConfigPost({
        strategyId,
        solveSwitch: value.solveSwitch,
        solveResolution: value.solveResolution,
        solveStartTime: value.solveStartTime,
      })
        .then(onConfirm)
        .finally(() => setConfirmLoading(false));
    });
  };

  return (
    <Modal
      title="算法运行配置"
      open={true}
      onOk={onOk}
      contentClassName="modal-form-content"
      confirmLoading={confirmLoading}
      onCancel={onCancel}
    >
      <Spin spinning={loading}>
        <Form form={form} className={styles.algorithmForm}>
          <Form.Item
            name="solveSwitch"
            initialValue={false}
            label="算法运行配置开关"
            rules={[{ required: true, message: '请选择算法运行配置开关' }]}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          {solveSwitch && (
            <>
              <Form.Item
                name="solveStartTime"
                label="算法运行数据开始日期"
                rules={[{ required: true, message: '请选择算法运行数据开始日期' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  disabledDate={disabledDate}
                  disabledTime={disabledTime}
                  showTime={{ format: 'YYYY-MM-DD HH' }}
                  format="YYYY-MM-DD HH时"
                />
              </Form.Item>

              <Form.Item
                label="算法运行数据定时任务"
                name="solveResolution"
                rules={[{ required: true, message: '请选择推算法运行数据定时任务' }]}
              >
                <Select>
                  <Select.Option value={SolveResolutionType.HOUR}>
                    {SolveResolutionTypeDisplay[SolveResolutionType.HOUR]}
                  </Select.Option>
                  <Select.Option value={SolveResolutionType.FIFTEEN}>
                    {SolveResolutionTypeDisplay[SolveResolutionType.FIFTEEN]}
                  </Select.Option>
                </Select>
              </Form.Item>
            </>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default AlgorithmConfigurationModal;
